import { request } from '@/utils/ajax';

export interface InheritedMember {
    userId: number;
    userDisplayName: string;
    userEmail: string;
    projectCount: number;
    profileCount: number;
    createDate: string | null;
}

export interface ProfileMembershipGrant {
    isAdmin: boolean;
}

export interface ProjectMembershipGrant {
    isAdmin: boolean;
    hasAnalytics: boolean;
    notificationsEnabled: boolean;
}

export interface InheritedMemberProfilePreview {
    id: number;
    name: string;
    isAdmin: boolean;
    skipReason: null | 'already-member' | 'excluded-inherited' | 'previously-removed';
}

export interface InheritedMemberProjectPreview {
    id: number;
    name: string;
    modules: string[];
    skipReason: null | 'already-member' | 'excluded-inherited' | 'previously-removed';
    isAdmin: boolean;
    hasAnalytics: boolean;
    permissionIds: number[];
}

export interface InheritedMemberPreview {
    profiles: { willAdd: InheritedMemberProfilePreview[]; skipped: InheritedMemberProfilePreview[] };
    projects: { willAdd: InheritedMemberProjectPreview[]; skipped: InheritedMemberProjectPreview[] };
}

export function getInheritedMembers(profileId: number): Promise<InheritedMember[]> {
    return request(`api/profile/${profileId}/inherited-members`).then((x) => x.json());
}

export function previewAddInheritedMember(
    profileId: number,
    userId: number,
    profileGrant: ProfileMembershipGrant | null,
    projectGrant: ProjectMembershipGrant | null
): Promise<InheritedMemberPreview> {
    return request(`api/profile/${profileId}/inherited-members/${userId}/preview`, {
        body: { profileGrant, projectGrant },
        method: 'POST',
        json: true,
    }).then((x) => x.json());
}

export function addInheritedMember(
    profileId: number,
    userId: number,
    profileGrant: ProfileMembershipGrant | null,
    projectGrant: ProjectMembershipGrant | null,
    excludeProjectIds: number[] = [],
    excludeProfileIds: number[] = []
): Promise<void> {
    return request(`api/profile/${profileId}/inherited-members`, {
        body: { userId, profileGrant, projectGrant, excludeProjectIds, excludeProfileIds },
        method: 'POST',
        json: true,
    }).then(() => undefined);
}

export interface InheritedMemberProject {
    projectId: number;
    projectName: string;
    modules: string[];
    isAdmin: boolean;
    hasAnalytics: boolean;
    notificationsEnabled: boolean;
}

export interface InheritedMemberProfile {
    profileId: number;
    profileName: string;
    isAdmin: boolean;
}

export function getInheritedMemberProjects(profileId: number, userId: number): Promise<InheritedMemberProject[]> {
    return request(`api/profile/${profileId}/inherited-members/${userId}/projects`).then((x) => x.json());
}

export function getInheritedMemberProfiles(profileId: number, userId: number): Promise<InheritedMemberProfile[]> {
    return request(`api/profile/${profileId}/inherited-members/${userId}/profiles`).then((x) => x.json());
}

export function editInheritedMember(
    profileId: number,
    userId: number,
    profileGrant: ProfileMembershipGrant | null,
    projectGrant: ProjectMembershipGrant | null
): Promise<void> {
    return request(`api/profile/${profileId}/inherited-members/${userId}`, {
        body: { profileGrant, projectGrant },
        method: 'PUT',
        json: true,
    }).then(() => undefined);
}

export function removeInheritedMember(profileId: number, userId: number) {
    return request(`api/profile/${profileId}/inherited-members/${userId}`, {
        method: 'DELETE',
        json: true,
    });
}

export function removeInheritedMemberFromProject(profileId: number, userId: number, projectId: number) {
    return request(`api/profile/${profileId}/inherited-members/${userId}/project/${projectId}`, {
        method: 'DELETE',
        json: true,
    });
}

export function removeInheritedMemberFromProfile(profileId: number, userId: number, descendantProfileId: number) {
    return request(`api/profile/${profileId}/inherited-members/${userId}/profile/${descendantProfileId}`, {
        method: 'DELETE',
        json: true,
    });
}
